import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';

export const MicrophoneLevel = ({ audioStream }) => {
  const [level, setLevel] = React.useState(0);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef();
  const maxObservedLevelRef = useRef(1); // To track the maximum observed level

  useEffect(() => {
    if (!audioStream) return;

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const microphone = audioContext.createMediaStreamSource(audioStream);
    microphone.connect(analyser);
    analyser.fftSize = 256;
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateLevel = () => {
      if (!analyserRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;

      // Update the maximum observed level
      maxObservedLevelRef.current = Math.max(
        maxObservedLevelRef.current,
        average,
      );

      // Normalize the level based on the maximum observed level
      const normalizedLevel = average / maxObservedLevelRef.current;

      // Amplify the normalized level for better visualization
      const amplifiedLevel = Math.min(100, normalizedLevel * 150);

      setLevel(amplifiedLevel);

      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };

    updateLevel();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      audioContext.close();
    };
  }, [audioStream]);

  const getActiveBars = () => {
    if (level >= 75) return 4;
    if (level >= 50) return 3;
    if (level >= 25) return 2;
    if (level > 0) return 1;
    return 0;
  };

  const activeBars = getActiveBars();

  const barStyle = (isActive) => ({
    height: 4,
    flex: 1,
    bgcolor: isActive ? 'primary.main' : 'divider',
    borderRadius: '2px',
    transition: 'background-color 0.1s ease-in-out',
  });

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        gap: '8px',
      }}
    >
      {[1, 2, 3, 4].map((barNumber) => (
        <Box key={barNumber} sx={barStyle(barNumber <= activeBars)} />
      ))}
    </Box>
  );
};

export default MicrophoneLevel;
